import type { FieldSelection, JavascriptSerializable } from '#layers/data-model/public';
import type { CollectionCodeSnippetOptions } from './types';
import { resolveFieldSelectionDefaults, toJavascriptSerializable } from '#layers/data-model/public';
import { getDefaultCodeFilter } from './defaults';
import { resolveCodeSelection } from './fields';
import { addDefined, resolveReturnSelection } from './sdk-payload';

/**
 * Builds the query parameters used by REST read snippets.
 */
export function createRestReadQuery(options: CollectionCodeSnippetOptions): { [key: string]: JavascriptSerializable } {
	const query: { [key: string]: JavascriptSerializable } = {};

	addRestFieldSelection(query, resolveCodeSelection(options));

	addDefined(query, 'filter', options.filter);

	if (options.operation !== 'readOne') {
		addDefined(query, 'sort', options.sort);
	}

	if (options.operation === 'readFirst') {
		query.limit = 1;
	}
	else if (options.operation === 'readMany') {
		addDefined(query, 'limit', options.limit);
		addDefined(query, 'offset', options.offset);
	}

	return query;
}

/**
 * Builds the query parameters used by REST write snippets.
 */
export function createRestWriteQuery(options: CollectionCodeSnippetOptions): { [key: string]: JavascriptSerializable } {
	const query: { [key: string]: JavascriptSerializable } = {};

	// Bulk writes need a filter. Use a schema-derived example instead of emitting an unconstrained operation.
	if (options.operation === 'updateMany' || options.operation === 'deleteMany') {
		query.filter = toJavascriptSerializable(options.filter ?? getDefaultCodeFilter(options.collection));
	}
	else {
		addDefined(query, 'filter', options.filter);
	}

	const selection = resolveReturnSelection(options);

	if (selection) {
		addRestFieldSelection(query, selection);
	}

	if (options.operation === 'updateMany') {
		addDefined(query, 'sort', options.sort);
		addDefined(query, 'limit', options.limit);
	}

	return query;
}

/**
 * Appends serialized query parameters to a REST endpoint.
 */
export function appendRestQuery(endpoint: string, query: { [key: string]: JavascriptSerializable }): string {
	const queryString = stringifyRestQuery(query);

	return queryString ? `${endpoint}?${queryString}` : endpoint;
}

/**
 * Adds scalar fields and nested include queries for REST snippets.
 */
function addRestFieldSelection(target: { [key: string]: JavascriptSerializable }, selection: FieldSelection): void {
	for (const [key, value] of Object.entries(resolveFieldSelectionDefaults(selection))) {
		target[key] = toJavascriptSerializable(value);
	}
}

/**
 * Serializes nested REST query values with bracket notation.
 */
function stringifyRestQuery(query: { [key: string]: JavascriptSerializable }): string {
	return flattenRestQuery(query)
		.map(([key, value]) => {
			const encodedValue = encodeURIComponent(String(value));

			return `${key}=${encodedValue}`;
		})
		.join('&');
}

/**
 * Flattens a nested query object into REST bracket-notation key/value pairs.
 */
function flattenRestQuery(value: JavascriptSerializable, path?: string): [key: string, value: string | number | boolean | null][] {
	if (value === undefined || (!path && (value === null || typeof value !== 'object' || Array.isArray(value)))) {
		return [];
	}

	if (value === null || typeof value !== 'object') {
		return path ? [[path, value]] : [];
	}

	if (Array.isArray(value)) {
		if (!path) return [];

		return value.flatMap((item, index) => {
			const itemPath = `${path}[${index}]`;

			return flattenRestQuery(item, itemPath);
		});
	}

	return Object.entries(value).flatMap(([key, nestedValue]) => {
		const nestedPath = path ? `${path}[${key}]` : key;

		return flattenRestQuery(nestedValue, nestedPath);
	});
}
